import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  Button,
  Sheet,
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  FormControl,
  FormLabel,
  Input,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/joy";
import { CheckRounded, DeleteRounded, EditRounded } from "@mui/icons-material";
import { toast } from "react-toastify";
import { LoadingView, PageTitle } from "../../../components";
import { useLocation } from "react-router-dom";
import { STATUS } from "../../../constant";
import { connect, useDispatch } from "react-redux";
import {
  merchantPropertiesRequest,
  merchantPropertiesReset,
  createMerchantPropertyRequest,
  createMerchantPropertyReset,
  updateMerchantPropertyRequest,
  updateMerchantPropertyReset,
  deleteMerchantPropertyRequest,
  deleteMerchantPropertyReset,
} from "../../../store/actions";


const MerchantPropertiesPage = ({
  loginResult,
  merchantPropertiesStatus,
  merchantPropertiesResult,
  merchantPropertiesErrorMessage,

  createMerchantPropertyStatus,
  createMerchantPropertyResult,
  createMerchantPropertyErrorMessage,

  updateMerchantPropertyStatus,
  updateMerchantPropertyResult,
  updateMerchantPropertyErrorMessage,

  deleteMerchantPropertyStatus,
  deleteMerchantPropertyResult,
  deleteMerchantPropertyErrorMessage,
}) => {
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { merchant_code } = state || {};

  const [properties, setProperties] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [propertyId, setPropertyId] = useState(null);
  const [propertyName, setPropertyName] = useState("");
  const [propertyValue, setPropertyValue] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetchMerchantProperties = () => {
    if (merchant_code) {
      dispatch(
        merchantPropertiesRequest(loginResult.token, {
          code: Number(merchant_code),
        }),
      );
    }
  };

  useEffect(() => {
    fetchMerchantProperties();
  }, [merchant_code, dispatch]);

  useEffect(() => {
    if (merchantPropertiesStatus === STATUS.SUCCESS && merchantPropertiesResult) {
      setProperties(merchantPropertiesResult);
    }
  }, [merchantPropertiesStatus, merchantPropertiesResult]);

  const checkLoading = () => {
    return (
      merchantPropertiesStatus === STATUS.LOADING ||
      createMerchantPropertyStatus === STATUS.LOADING ||
      updateMerchantPropertyStatus === STATUS.LOADING ||
      deleteMerchantPropertyStatus === STATUS.LOADING
    );
  };

  useEffect(() => {
    if (merchantPropertiesStatus === STATUS.ERROR) {
      toast.error(merchantPropertiesErrorMessage, {
        toastId: "merchantPropertiesError",
      });
      dispatch(merchantPropertiesReset());
    }

    if (createMerchantPropertyStatus === STATUS.ERROR) {
      toast.error(createMerchantPropertyErrorMessage, {
        toastId: "createMerchantPropertyError",
      });
      dispatch(createMerchantPropertyReset());
    }

    if (createMerchantPropertyStatus === STATUS.SUCCESS) {
      toast.success("Property created successfully", {
        toastId: "createMerchantPropertySuccess",
      });
      dispatch(createMerchantPropertyReset());
      fetchMerchantProperties();
    }

    if (updateMerchantPropertyStatus === STATUS.ERROR) {
      toast.error(updateMerchantPropertyErrorMessage, {
        toastId: "updateMerchantPropertyError",
      });
      dispatch(updateMerchantPropertyReset());
    }

    if (updateMerchantPropertyStatus === STATUS.SUCCESS) {
      toast.success("Property updated successfully", {
        toastId: "updateMerchantPropertySuccess",
      });
      dispatch(updateMerchantPropertyReset());
      fetchMerchantProperties();
    }

    if (deleteMerchantPropertyStatus === STATUS.ERROR) {
      toast.error(deleteMerchantPropertyErrorMessage, {
        toastId: "deleteMerchantPropertyError",
      });
      dispatch(deleteMerchantPropertyReset());
    }

    if (deleteMerchantPropertyStatus === STATUS.SUCCESS) {
      toast.success("Property deleted successfully", {
        toastId: "deleteMerchantPropertySuccess",
      });
      dispatch(deleteMerchantPropertyReset());
      fetchMerchantProperties();
    }
  }, [
    merchantPropertiesStatus,
    merchantPropertiesErrorMessage,
    createMerchantPropertyStatus,
    createMerchantPropertyErrorMessage,
    updateMerchantPropertyStatus,
    updateMerchantPropertyErrorMessage,
    deleteMerchantPropertyStatus,
    deleteMerchantPropertyErrorMessage,
    dispatch,
  ]);

  const handleCreate = () => {
    setPropertyId(null);
    setPropertyName("");
    setPropertyValue("");
    setFormOpen(true);
    setEditing(false);
  };

  const handleUpdate = (id, name, value) => {
    setPropertyId(id);
    setPropertyName(name);
    setPropertyValue(value);
    setFormOpen(true);
    setEditing(true);
  };

  const handleSave = () => {
    if (editing && propertyId) {
      dispatch(
        updateMerchantPropertyRequest(loginResult.token, {
          id: Number(propertyId),
          code: Number(merchant_code),
          name: propertyName,
          value: propertyValue,
        }),
      );
    } else {
      dispatch(
        createMerchantPropertyRequest(loginResult.token, {
          code: Number(merchant_code),
          name: propertyName,
          value: propertyValue,
        }),
      );
    }

    setFormOpen(false);
    setEditing(false);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const confirmDelete = () => {
    dispatch(
      deleteMerchantPropertyRequest(loginResult.token, {
        id: Number(deleteId),
        code: Number(merchant_code),
      }),
    );
    setDeleteOpen(false);
  };

  return (
    <Box>
      <PageTitle title="Merchant Properties" />

      <Box sx={{ mt: 3 }}>
        <Button onClick={handleCreate} color="primary">
          <CheckRounded /> Add Property
        </Button>
      </Box>

      {/* Properties Table */}
      <LoadingView loading={checkLoading()} />
      {merchantPropertiesResult && (
        <Sheet
          className="OrderTableContainer"
          variant="outlined"
          sx={{
            display: "flex",
            borderRadius: "sm",
            flexShrink: 1,
            overflow: "auto",
            minHeight: 0,
            mt: 2,
          }}
        >
          <Table
            aria-labelledby="tableTitle"
            sx={{
              "--TableCell-paddingY": "4px",
              "--TableCell-paddingX": "8px",
            }}
          >
            <thead>
              <tr>
                <th style={{ width: "10%", fontSize: 16 }}>#</th>
                <th style={{ width: "40%", fontSize: 16 }}>Name</th>
                <th style={{ width: "35%", fontSize: 16 }}>Value</th>
                <th style={{ width: "15%", fontSize: 16 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {properties.map((prop, index) => (
                <tr key={prop.id || index}>
                  <td>{index + 1}</td>
                  <td>{prop.name || ""}</td>
                  <td>{prop.value || ""}</td>
                  <td>
                    <Stack direction="row" spacing={1} justifyContent="flex-start">
                      <Tooltip title="Edit" variant="outlined">
                        <IconButton
                          size="sm"
                          variant="soft"
                          color="neutral"
                          onClick={() =>
                            handleUpdate(prop.id, prop.name || "", prop.value || "")
                          }
                        >
                          <EditRounded />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" variant="outlined">
                        <IconButton
                          size="sm"
                          variant="soft"
                          color="danger"
                          onClick={() => handleDelete(prop.id)}
                        >
                          <DeleteRounded />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      )}

      {/* Create/Update Form Modal */}
      <Modal open={formOpen}>
        <ModalDialog
          sx={(theme) => ({
            [theme.breakpoints.only("xs")]: {
              bottom: 0,
              left: 0,
              right: 0,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              transform: "none",
              maxWidth: "unset",
            },
          })}
        >
          <ModalClose variant="outlined" onClick={() => setFormOpen(false)} />
          <Stack sx={{ mt: 2 }}>
            <Typography>
              {editing ? "Edit Property" : "Add Property"}
            </Typography>
            <Stack gap={2}>
              <FormControl sx={{ flex: 1 }} required>
                <FormLabel>Property Name</FormLabel>
                <Input
                  value={propertyName}
                  onChange={(e) => setPropertyName(e.target.value)}
                  placeholder="e.g., Main Bank Account"
                />
              </FormControl>

              <FormControl sx={{ flex: 1 }} required>
                <FormLabel>Property Value</FormLabel>
                <Input
                  value={propertyValue}
                  onChange={(e) => setPropertyValue(e.target.value)}
                  placeholder="e.g., 1234567890"
                />
              </FormControl>

              <Button type="button" fullWidth sx={{ mt: 3 }} onClick={handleSave}>
                {editing ? "Update" : "Create"}
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={deleteOpen}>
        <ModalDialog
          sx={(theme) => ({
            [theme.breakpoints.only("xs")]: {
              bottom: 0,
              left: 0,
              right: 0,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              transform: "none",
              maxWidth: "unset",
            },
          })}
        >
          <ModalClose variant="outlined" onClick={() => setDeleteOpen(false)} />
          <Stack sx={{ mt: 2, textAlign: "center" }}>
            <Typography>Delete Property</Typography>
            <Typography>Are you sure you want to delete this property?</Typography>
            <Stack gap={2}>
              <Button type="button" fullWidth color="danger" onClick={confirmDelete}>
                DELETE
              </Button>
              <Button type="button" fullWidth color="primary" onClick={() => setDeleteOpen(false)}>
                CANCEL
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

const mapStateToProps = ({ merchant, auth }) => {
  const { otpResult: loginResult } = auth;
  const {
    merchantPropertiesStatus,
    merchantPropertiesResult,
    merchantPropertiesErrorMessage,

    createMerchantPropertyStatus,
    createMerchantPropertyResult,
    createMerchantPropertyErrorMessage,

    updateMerchantPropertyStatus,
    updateMerchantPropertyResult,
    updateMerchantPropertyErrorMessage,

    deleteMerchantPropertyStatus,
    deleteMerchantPropertyResult,
    deleteMerchantPropertyErrorMessage,
  } = merchant;

  return {
    loginResult,

    merchantPropertiesStatus,
    merchantPropertiesResult,
    merchantPropertiesErrorMessage,

    createMerchantPropertyStatus,
    createMerchantPropertyResult,
    createMerchantPropertyErrorMessage,

    updateMerchantPropertyStatus,
    updateMerchantPropertyResult,
    updateMerchantPropertyErrorMessage,

    deleteMerchantPropertyStatus,
    deleteMerchantPropertyResult,
    deleteMerchantPropertyErrorMessage,
  };
};

export default connect(mapStateToProps, {
  merchantPropertiesRequest,
  merchantPropertiesReset,
  createMerchantPropertyRequest,
  createMerchantPropertyReset,
  updateMerchantPropertyRequest,
  updateMerchantPropertyReset,
  deleteMerchantPropertyRequest,
  deleteMerchantPropertyReset,
})(MerchantPropertiesPage);